import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Chip, Group, Text } from '@common/ui'

export type LogLevel = 'log' | 'warn' | 'error'
export type LogEntry = { level: LogLevel; time: string; args: unknown[] }

const LEVEL_COLORS: Record<LogLevel, { bg: string; color: string }> = {
  log: { bg: 'transparent', color: 'var(--trellis-color-text-primary)' },
  warn: { bg: 'var(--trellis-color-warning-subtle)', color: 'var(--trellis-color-warning)' },
  error: { bg: 'var(--trellis-color-error-subtle)', color: 'var(--trellis-color-error)' },
}

const formatArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.message}\n${arg.stack ?? ''}`
  try {
    return JSON.stringify(arg, null, 2)
  } catch {
    return String(arg)
  }
}

export const useLogEntries = () => {
  const [entries, setEntries] = useState<LogEntry[]>([])

  const addEntry = useCallback((level: LogLevel, args: unknown[]) => {
    const now = new Date()
    const time = now.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    setEntries((prev) => [...prev, { level, time, args }])
  }, [])

  const clear = useCallback(() => setEntries([]), [])

  return { entries, addEntry, clear }
}

const DEFAULT_HEIGHT = 200
const MIN_HEIGHT = 50

const LogPanel = ({ entries, onClear }: { entries: LogEntry[]; onClear: () => void }) => {
  const [expanded, setExpanded] = useState(false)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<{ y: number; height: number } | null>(null)

  useEffect(() => {
    if (expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [entries, expanded])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      dragStartRef.current = { y: e.clientY, height }
    },
    [height]
  )

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    setHeight(Math.max(MIN_HEIGHT, dragStartRef.current.height + (dragStartRef.current.y - e.clientY)))
  }, [])

  const onPointerUp = useCallback(() => {
    dragStartRef.current = null
  }, [])

  const errorCount = entries.filter((e) => e.level === 'error').length
  const warnCount = entries.filter((e) => e.level === 'warn').length

  return (
    <div
      style={{
        borderTop: '1px solid var(--trellis-color-outline)',
        backgroundColor: 'var(--trellis-color-surface-variant)',
        fontSize: 12,
      }}
    >
      {expanded && (
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          style={{
            height: 6,
            cursor: 'row-resize',
            borderBottom: '1px solid var(--trellis-color-outline)',
            touchAction: 'none',
          }}
        />
      )}
      <Group
        onClick={() => setExpanded((v) => !v)}
        justify="space-between"
        px="sm"
        py="xxs"
        style={{ cursor: 'pointer', userSelect: 'none', minHeight: 28 }}
      >
        <Group gap="xxs">
          <Text size="xs" fw={600}>
            Logs
          </Text>
          <Chip size="xs">{entries.length}</Chip>
          {errorCount > 0 && (
            <Chip size="xs" tone="negative">{`${errorCount} error${errorCount !== 1 ? 's' : ''}`}</Chip>
          )}
          {warnCount > 0 && <Chip size="xs" tone="caution">{`${warnCount} warning${warnCount !== 1 ? 's' : ''}`}</Chip>}
        </Group>
        <Button
          variant="subtle"
          size="compact-xs"
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
        >
          Clear
        </Button>
      </Group>
      {expanded && (
        <div
          ref={scrollRef}
          style={{
            height,
            overflowY: 'auto',
            borderTop: '1px solid var(--trellis-color-outline)',
            backgroundColor: 'var(--trellis-color-surface)',
          }}
        >
          {entries.length === 0 ? (
            <Text size="xs" c="dimmed" ta="center" p="sm" display="block">
              No log entries
            </Text>
          ) : (
            entries.map((entry, i) => {
              const { bg, color } = LEVEL_COLORS[entry.level]
              const text = entry.args.map(formatArg).join(' ')
              const prefixMatch = text.match(/^\[([^\]]+)\]\s*/)
              return (
                <div
                  key={i}
                  style={{
                    paddingInline: 12,
                    paddingBlock: 3,
                    fontFamily: 'var(--trellis-font-mono)',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    backgroundColor: bg,
                    color,
                  }}
                >
                  <span style={{ color: 'var(--trellis-color-text-tertiary)', marginRight: 8 }}>{entry.time}</span>
                  {prefixMatch && (
                    <span style={{ color: 'var(--trellis-color-primary)', marginRight: 8 }}>{prefixMatch[1]}</span>
                  )}
                  {prefixMatch ? text.slice(prefixMatch[0].length) : text}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

export default LogPanel
